import { ref } from 'vue'
import { TableDataQuery, getData } from '@/app/core/services/get-table'
import { Field } from '@/app/core/services/forms'
import { validateTranslate } from '@/app/core/composable/general'
import { isUUID, snakeToCamelObject } from '@/app/core/util/strings'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

export default function useDraggableList() {
  const loadingData = ref(false)
  const columns = ref<any[]>([])
  const field = ref<Field>()
  const rows = ref<any[]>([])
  const totalRows = ref(0)

  const showModal = ref(false)
  const titleModal = ref('')
  const descriptionModal = ref('')
  const typeAlertModal = ref('')

  const loadingReport = ref(false)
  const messageLoading = ref('')
  const urlFile = ref(null)

  const loadData = async (currentId: string) => {
    if (!field.value?.query || !currentId) return
    loadingData.value = true

    let where: string[] = [...(field.value?.query?.where ?? [])]
    where = where.map(_ => _.replaceAll(/:current/g, `'${currentId ?? ''}'`))

    let modifier: string[] = [...(field.value?.query?.modifier ?? [])]

    const hasOrderBy = modifier.some((_: string) =>
      _.toUpperCase().includes('ORDER')
    )

    if (!hasOrderBy) {
      const orderName = field.value?.query?.fields
        ?.filter((_: string) => _ !== 'id')
        .map((field: string) => {
          if (field.split(' as ')?.[0].split('.')[1] === undefined) {
            return field.split(' as ')?.[0].split('.')[1]
          } else {
            return field.split(' as ')?.[0].split('.')[0]
          }
        })

      if (orderName.indexOf('name') >= 1) modifier.push(`ORDER BY name DESC`)
    }

    try {
      const query: TableDataQuery = {
        tables: [...(field.value?.query?.tables ?? [])],
        join: [...(field.value?.query?.join ?? [])],
        fields: [...(field.value?.query?.fields ?? [])],
        where,
        options: {
          outputFormat: 'json'
        },
        modifier
      }
      const data: any = await getData(query)
      rows.value = data[0].results
      totalRows.value = data[0].totalItems
    } finally {
      loadingData.value = false
    }
  }

  const loadRow = async (id: string, currentId?: string) => {
    try {
      let recordId = id
      if (field.value?.query?.groupedBy) {
        recordId = currentId || id
      }

      const query: TableDataQuery = {
        tables: [...(field.value?.query?.tables ?? [])],
        fields: ['*'],
        where: [`id = '${recordId}'`]
      }
      const data: any = await getData(query)
      let results = data[0]?.results[0] ?? []
      if (field.value?.query?.groupedBy) {
        results =
          results[field.value?.query?.groupedBy].find(
            (_: any) => _?.id === id
          ) ?? []
      }
      return snakeToCamelObject(results)
    } catch (error: any) {
      console.error(error)
    }
  }

  const setField = (f: Field) => {
    field.value = f ?? {}
    setColumns()
  }

  const setColumns = () => {
    columns.value.push(
      ...(field.value?.props?.columns
        .map(
          (col: {
            name: string
            title: string
            replace: string | null
            options: object[] | null
          }) => {
            return {
              name: col.name,
              title: validateTranslate(col.title),
              table: col.name,
              replace: col.replace,
              options: Array.isArray(col.options)
                ? col.options.map((opt: any) => ({
                    id: opt.id,
                    name: opt.name
                  }))
                : null
            }
          }
        )
        ?.filter((_: any) => _.title !== 'ID') ?? []),
      { name: 'actions', title: '' }
    )
  }

  const getTableName = () => {
    return field.value?.query?.tables?.[0].replace('pg_', '')
  }

  const replaceIdsByName = async (obj: any) => {
    const newObj: any = {}
    for (const key in obj) {
      const column = columns.value?.find((_: any) => _.name === key)
      if (column?.replace && obj[key] !== null) {
        if (isUUID(obj[key])) {
          const [table, ...fields] = column.replace.split('.')
          const query: TableDataQuery = {
            tables: ['pg_'.concat(table)],
            fields: [fields.join('.')],
            where: ["id = '".concat(obj[key]).concat("'")]
          }
          const data: any = await getData(query)
          const result = data[0]?.results?.[0]
          if (result) {
            const value = Object.values(result)[0]
            newObj[key] = value
          } else {
            newObj[key] = obj[key]
          }
        } else {
          newObj[key] = obj[key]
        }
      } else if (column?.options) {
        newObj[key] =
          column.options.find((option: any) => option.id === obj[key])?.name ||
          obj[key]
      } else {
        newObj[key] = obj[key]
      }
    }
    return newObj
  }

  const closeModalReport = () => {
    showModal.value = false
    urlFile.value = null
  }

  return {
    loadingData,
    columns,
    rows,
    totalRows,
    loadData,
    loadRow,
    setField,
    setColumns,
    getTableName,
    replaceIdsByName,
    loadingReport,
    messageLoading,
    showModal,
    typeAlertModal,
    titleModal,
    descriptionModal,
    closeModalReport,
    urlFile
  }
}
